/**
 * Validation utilities for CreateAuditSessionDialog
 *
 * Extracted validation logic to reduce component complexity and improve reusability.
 * All dates are handled in UTC.
 */

import { MAX_DATE_RANGE_DAYS, DATE_VALIDATION_ERRORS } from '@core/utils';
import type { SampleFilters } from '@internal-audit/domain';

/**
 * Get default start date (30 days ago in UTC)
 */
export const getDefaultStartDate = (): string => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

/**
 * Get default end date (today in UTC)
 */
export const getDefaultEndDate = (): string => new Date().toISOString().split('T')[0];

/**
 * Get minimum allowed start date (90 days ago from today in UTC)
 * This allows selecting a range that spans up to 90 days inclusive
 */
export const getMinStartDate = (): string => {
  const today = new Date();
  const minStartDate = new Date(today);

  // Subtract 90 days to get the minimum allowed start date
  minStartDate.setUTCDate(today.getUTCDate() - MAX_DATE_RANGE_DAYS);

  return minStartDate.toISOString().split('T')[0];
};

/**
 * Get maximum allowed end date (today in UTC)
 */
export const getMaxEndDate = (): string => new Date().toISOString().split('T')[0];

/**
 * Get initial empty filters
 */
export const getInitialFilters = (): SampleFilters => ({
  languages: [],
  topics: [],
  labels: [],
  labelSeverities: [],
  contentTypes: [],
});

/**
 * Validate date range for audit session (all dates treated as UTC)
 * - End date cannot be in the future (compared at day level)
 * - Start date cannot be earlier than allowed minimum (compared at day level)
 * - End date cannot be before start date
 */
export const validateDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';

  // Parse dates as UTC by appending time and 'Z'
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);

  // Use same min/max as UI for consistency
  const minStart = new Date(`${getMinStartDate()}T00:00:00Z`);
  const maxEnd = new Date(`${getMaxEndDate()}T00:00:00Z`);

  // Check if end date is in the future
  if (end > maxEnd) {
    return DATE_VALIDATION_ERRORS.END_DATE_FUTURE;
  }

  // Check if start date is earlier than minimum allowed
  if (start < minStart) {
    return DATE_VALIDATION_ERRORS.START_DATE_TOO_OLD;
  }

  // Check if end date is before start date
  if (end < start) {
    return DATE_VALIDATION_ERRORS.END_BEFORE_START;
  }

  return '';
};

/**
 * Check if form is valid for submission
 */
export const isFormValid = (params: { sessionName: string; startDate: string; endDate: string; dateError: string }): boolean =>
  Boolean(params.sessionName.trim() && params.startDate && params.endDate && !params.dateError);
